import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  StyleSheet,
  View,
  Platform,
  Animated,
  Text,
} from 'react-native';
import { Tabs, useRouter, useFocusEffect } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { StatusBar } from 'expo-status-bar';
import AsyncStorage from '@react-native-async-storage/async-storage';
import NetInfo, { NetInfoState } from '@react-native-community/netinfo';

// Même composant CustomTabBar que la navbar principale, et même contexte
// TabBarVisibilityContext (rapatrié dans ce fichier, pas dans un dossier
// contexts/ séparé — Metro ne le résolvait pas correctement sur Vercel).
// Ce sous-layout expose son PROPRE Provider (voir plus bas) : les écrans
// enfants (index.tsx, boutiques.tsx, reglages.tsx) qui appellent
// useTabBarVisibility() récupèrent CE setTabBarHidden-ci, pas celui du
// layout parent (déjà caché dès qu'on entre dans Market).
import CustomTabBar, { type TabBarButtonConfig, TabBarVisibilityContext, useTabBarVisibility } from '@/components/CustomTabBar';
import { useTranslation } from 'react-i18next';

const OFFLINE_BANNER_HEIGHT = 36;

const MARKET_TAB_CONFIG: TabBarButtonConfig[] = [
  { key: 'home',      icon: 'home-outline',       iconActive: 'home' },
  { key: 'index',     icon: 'storefront-outline',  iconActive: 'storefront' },
  { key: 'boutiques', icon: 'bag-outline',         iconActive: 'bag' },
  { key: 'reglages',  icon: 'settings-outline',    iconActive: 'settings' },
];

export default function MarketTabsLayout() {
  const router = useRouter();
  const { t } = useTranslation();

  const { setTabBarHidden: setParentTabBarHidden } = useTabBarVisibility();

  const [parentNavbarHidden, setParentNavbarHidden] = useState(false);

  useFocusEffect(
    useCallback(() => {
      setParentNavbarHidden(false);

      setParentTabBarHidden(true, () => {
        setParentNavbarHidden(true);
      });

      return () => {
        setParentTabBarHidden(false);
      };
    }, [setParentTabBarHidden])
  );

  const [sessionChecked, setSessionChecked] = useState(false);

  const accessTokenRef  = useRef<string>('');
  const refreshTokenRef = useRef<string>('');
  const expiresAtRef    = useRef<number>(0);

  useEffect(() => {
    const initSession = async () => {
      try {
        const raw = await AsyncStorage.getItem('harmonia_session');

        if (!raw) { router.replace('/login'); return; }

        const session = JSON.parse(raw);

        if (!session?.access_token) { router.replace('/login'); return; }

        accessTokenRef.current  = session.access_token  || '';
        refreshTokenRef.current = session.refresh_token || '';
        expiresAtRef.current    = session.expires_at    || 0;

        setSessionChecked(true);
      } catch (error) {
        console.error('Market auth check error:', error);
        router.replace('/login');
      }
    };

    initSession();
  }, [router]);

  const [isOnline, setIsOnline] = useState(true);
  const [isTabBarHidden, setIsTabBarHidden] = useState(false);
  const [isTabBarFullyHidden, setIsTabBarFullyHidden] = useState(false);

  const offlineBannerAnim = useRef(new Animated.Value(0)).current;
  const tabBarTranslateY  = useRef(new Animated.Value(0)).current;

  const [measuredTabBarHeight, setMeasuredTabBarHeight] = useState(100);
  const measuredTabBarHeightRef = useRef(100);

  const updateOnlineStatus = useCallback((online: boolean) => {
    setIsOnline(online);
    Animated.timing(offlineBannerAnim, {
      toValue:         online ? 0 : 1,
      duration:        350,
      useNativeDriver: false,
    }).start();
  }, [offlineBannerAnim]);

  useEffect(() => {
    if (Platform.OS === 'web') {
      const handleOnline  = () => updateOnlineStatus(true);
      const handleOffline = () => updateOnlineStatus(false);
      window.addEventListener('online',  handleOnline);
      window.addEventListener('offline', handleOffline);
      updateOnlineStatus(navigator.onLine);
      return () => {
        window.removeEventListener('online',  handleOnline);
        window.removeEventListener('offline', handleOffline);
      };
    } else {
      const unsubscribe = NetInfo.addEventListener((state: NetInfoState) => {
        const connected = state.isConnected === true && state.isInternetReachable !== false;
        updateOnlineStatus(connected);
      });
      NetInfo.fetch().then((state: NetInfoState) => {
        const connected = state.isConnected === true && state.isInternetReachable !== false;
        updateOnlineStatus(connected);
      });
      return () => unsubscribe();
    }
  }, [updateOnlineStatus]);

  const tabBarShadowOpacity = useRef(new Animated.Value(1)).current;

  const setTabBarHidden = useCallback((hidden: boolean) => {
    setIsTabBarHidden(hidden);

    if (!hidden) setIsTabBarFullyHidden(false);

    Animated.parallel([
      Animated.timing(tabBarTranslateY, {
        toValue: hidden ? measuredTabBarHeightRef.current + 20 : 0,
        duration: 300,
        useNativeDriver: true,
      }),
      Animated.timing(tabBarShadowOpacity, {
        toValue: hidden ? 0 : 1,
        duration: 300,
        useNativeDriver: true,
      }),
    ]).start(({ finished }) => {
      if (finished && hidden) setIsTabBarFullyHidden(true);
    });
  }, [tabBarTranslateY, tabBarShadowOpacity]);

  const bannerHeight = offlineBannerAnim.interpolate({
    inputRange:  [0, 1],
    outputRange: [0, OFFLINE_BANNER_HEIGHT],
  });
  const bannerOpacity = offlineBannerAnim.interpolate({
    inputRange:  [0, 0.5, 1],
    outputRange: [0, 0,   1],
  });

  const handleInterceptPress = useCallback((key: string) => {
    if (key === 'home') {
      setParentTabBarHidden(false);
      router.replace('/(tabs)/actu');
    }
  }, [router, setParentTabBarHidden]);

  if (!sessionChecked) {
    return (
      <View style={styles.loadingContainer}>
        <Ionicons name="sync" size={40} color="#8A2BE2" />
      </View>
    );
  }

  return (
    <TabBarVisibilityContext.Provider value={{ setTabBarHidden }}>
      <View style={styles.container}>
        <StatusBar style="light" />

        {/* ── Bandeau OFFLINE ── */}
        <Animated.View
          style={[styles.offlineBanner, { height: bannerHeight, opacity: bannerOpacity }]}
          pointerEvents={isOnline ? 'none' : 'auto'}
        >
          <Ionicons name="wifi" size={14} color="#fff" style={styles.offlineIcon} />
          <Text style={styles.offlineText}>{t('market.common.offlineText')}</Text>
        </Animated.View>

        <Tabs
          tabBar={(props) => (
            <CustomTabBar
              {...props}
              isOnline={isOnline}
              tabBarTranslateY={tabBarTranslateY}
              tabBarShadowOpacity={tabBarShadowOpacity}
              isTabBarFullyHidden={isTabBarFullyHidden}
              buttonsConfig={MARKET_TAB_CONFIG}
              interceptKeys={['home']}
              onInterceptPress={handleInterceptPress}
              onMeasureHeight={(h: number) => {
                if (h > 0 && Math.abs(h - measuredTabBarHeightRef.current) > 1) {
                  measuredTabBarHeightRef.current = h;
                  setMeasuredTabBarHeight(h);
                }
              }}
            />
          )}
          screenOptions={{ headerShown: false }}
        >
          <Tabs.Screen name="index"     options={{ title: 'Marché' }} />
          <Tabs.Screen name="boutiques" options={{ title: 'Boutiques' }} />
          <Tabs.Screen name="reglages"  options={{ title: 'Réglages' }} />
        </Tabs>
      </View>
    </TabBarVisibilityContext.Provider>

  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F5F5F5',
  },

  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F5F5F5',
  },

  offlineBanner: {
    backgroundColor: '#D32F2F',
    flexDirection:   'row',
    alignItems:      'center',
    justifyContent:  'center',
    overflow:        'hidden',
    zIndex:          100,
    width:           '100%' as any,
  },
  offlineIcon: { marginRight: 6, opacity: 0.9 },
  offlineText: { color: '#fff', fontSize: 13, fontWeight: '600', letterSpacing: 0.3 },
});
